import { useEffect } from 'react'
import { DetailsForConversationProps, Pagination, RequestForQuote, User } from '../../types'
import { SessionStore } from '../types'

import {
  FetchConversationsDocument,
  FetchMessagesForConversationDocument,
  useCreateConversationMutation,
  useCreateMessageMutation,
  useFetchMessagesForConversationQuery,
  useReadReceiptsForConversationQuery,
  RequestForQuoteStep,
} from '@/suppliers/_gen/gql'
import useGqlClient from '../hooks/useGqlClient'

import ConversationM from '../modules/Conversation'
import RequestForQuoteContext from '@/gf/modules/RequestForQuote'

import InboxCommon, { Conversation } from '@/gf/components/Inbox'
import InboxDetails from './InboxDetails'

const RequestForQuoteInbox = ({
  conversations,
  refetchConversations,
  page,
  setPage,
  pagination,
  user,
  requestForQuote,
  store,
  vendorId,
  vendorUserId,
  DetailsForConversation,
  selectedConversationId,
  onSelectedConversationIdChange,
  displayCustomerInfo = false,
  customerSelected,
}: {
  conversations: Conversation[]
  refetchConversations: () => Promise<unknown>
  page: number
  setPage: (page: number) => void
  pagination: Pagination
  user: Pick<User, 'id' | 'role'>
  requestForQuote: RequestForQuote & { step: RequestForQuoteStep }
  store: SessionStore
  vendorId?: string
  vendorUserId?: string
  DetailsForConversation?: (props: DetailsForConversationProps) => JSX.Element
  selectedConversationId: string | undefined
  onSelectedConversationIdChange: (conversationId: string | undefined) => void
  displayCustomerInfo?: boolean
  customerSelected?: boolean
}) => {
  const gqlClient = useGqlClient()
  const [createMessage] = useCreateMessageMutation({
    client: gqlClient,
    refetchQueries: [FetchMessagesForConversationDocument, FetchConversationsDocument],
  })
  const [createConversation] = useCreateConversationMutation({
    client: gqlClient,
    refetchQueries: [FetchConversationsDocument],
  })

  const displayCustomerContactInfo = displayCustomerInfo
    ? true
    : RequestForQuoteContext.canDisplayCustomerContactInfo(requestForQuote, user)

  const newConversationsUnfiltered = [
    // Non-Admin conversations
    ConversationM.newNonAdminConversationRfq(requestForQuote, store, displayCustomerContactInfo),
    // Admin conversations
    ConversationM.newAdminConversationRfq(requestForQuote, store),
  ]
  // Filter out existing conversations
  const newConversations = ConversationM.filterNewConversations(
    newConversationsUnfiltered,
    conversations
  )

  useEffect(() => {
    if (!selectedConversationId && customerSelected) {
      const customerConversation =
        conversations.find((conversation) => !conversation.admin) ??
        newConversations.find((conversation) => !conversation.admin)
      if (customerConversation) {
        onSelectedConversationIdChange(customerConversation.id)
      }
    }
  }, [
    selectedConversationId,
    customerSelected,
    conversations,
    newConversations,
    onSelectedConversationIdChange,
  ])

  const selectedConversation =
    conversations &&
    [...conversations, ...newConversations].find((c) => c.id === selectedConversationId)

  const { error: messagesError, ...messagesQueryResult } = useFetchMessagesForConversationQuery({
    client: gqlClient,
    variables: {
      conversationId: selectedConversation?.id as string,
      vendorId: vendorId ?? null,
      vendorUserId: vendorUserId ?? null,
    },
    pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
    // Only refetch if the conversation is unread, so we can fetch the correct "read" status
    onCompleted: selectedConversation?.unreadMessages ? refetchConversations : undefined,
    skip:
      !selectedConversationId || !selectedConversation || 'newConversation' in selectedConversation,
  })

  const { fetchMessagesForConversation: messages } = messagesQueryResult.data || {}

  const { readReceiptsForConversation } =
    useReadReceiptsForConversationQuery({
      client: gqlClient,
      variables: {
        conversationId: selectedConversation?.id as string,
        vendorId: vendorId ?? null,
        vendorUserId: vendorUserId ?? null,
      },
      pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
      skip:
        !selectedConversationId ||
        !selectedConversation ||
        'newConversation' in selectedConversation,
    }).data || {}

  return (
    <InboxCommon
      className="mt-4"
      user={user}
      conversations={conversations}
      selectedConversation={selectedConversation}
      selectedConversationId={selectedConversationId}
      messages={messages}
      messagesError={messagesError}
      readReceiptsForConversation={readReceiptsForConversation}
      onSelectedConversationIdChange={onSelectedConversationIdChange}
      page={page}
      setPage={setPage}
      pagination={pagination}
      newConversations={newConversations}
      displayCustomerContactInfo={displayCustomerContactInfo}
      DetailsForConversation={
        DetailsForConversation ?? InboxDetails.RequestForQuote(requestForQuote, store.id)
      }
      conversationRowContent={ConversationM.defaultConversationRowContent}
      createMessage={(vars) =>
        createMessage({
          variables: { ...vars, vendorId: vendorId ?? null, vendorUserId: vendorUserId ?? null },
        })
      }
      createConversation={(vars) =>
        createConversation({
          variables: { ...vars, vendorId: vendorId ?? null, vendorUserId: vendorUserId ?? null },
        }).then((resp) => resp.data?.createConversation)
      }
    />
  )
}

export default RequestForQuoteInbox
